<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { getSmsCode, loginByPhone } from '@/api/common';
import { batchAddDress, getDressTypeList } from '@/api/dress';
import { uploadImage } from '@/api/upload';
import { analyzeDressPng } from '@/utils/dressColorHint';
import { useAuthStore } from '@/store/auth';
import { useSettingStore } from '@/store/setting';
import { useUserStore } from '@/store/user';
import type { API } from '@/types';
import {
  compressPngUnderLimit,
  dressOriginMaxBytes,
  dressThumbMaxBytes,
  generateThumbnail,
  parsePsdFile,
  revokeParsedItemUrls,
  type ParsedDressItem,
  type PsdBounds,
  type PsdCanvasSize,
  type PsdParseProgress,
} from '@/utils/psdWebEngine';

type Step = 'upload' | 'preview' | 'confirm';
type ThumbEditSource = {
  blob: Blob;
  url: string;
  bounds: PsdBounds;
  canvas: PsdCanvasSize;
  ownedUrl: boolean;
};
type FilePickerHandle = {
  getFile: () => Promise<File>;
};
type FilePickerWindow = Window & {
  showOpenFilePicker?: (options?: {
    multiple?: boolean;
    excludeAcceptAllOption?: boolean;
    types?: Array<{
      description?: string;
      accept: Record<string, string[]>;
    }>;
  }) => Promise<FilePickerHandle[]>;
};

const psdFileAccept = '.psd,application/octet-stream,application/x-photoshop,application/photoshop,image/vnd.adobe.photoshop';
const psdPickerTypes = [
  {
    description: 'PSD 文件',
    accept: {
      'image/vnd.adobe.photoshop': ['.psd'],
      'application/x-photoshop': ['.psd'],
      'application/octet-stream': ['.psd'],
    },
  },
];

const authStore = useAuthStore();
const userStore = useUserStore();
const settingStore = useSettingStore();
const phone = ref('');
const smsCode = ref('');
const smsId = ref('');
const countdown = ref(0);
const loginLoading = ref(false);
const pageLoading = ref(false);
const parseLoading = ref(false);
const submitLoading = ref(false);
const loginError = ref('');
const flowError = ref('');
const progress = ref<PsdParseProgress | null>(null);
const step = ref<Step>('upload');
const items = ref<ParsedDressItem[]>([]);
const dressTypes = ref<API.DressType[]>([]);
const typePickerItemId = ref('');
const editingItemId = ref('');
const pendingDeleteItemId = ref('');
const showUsageGuide = ref(false);
const showPreviewBackConfirm = ref(false);
const showSubmitSuccess = ref(false);
const imagePreview = ref<{ url: string; title: string } | null>(null);
const editBackground = ref('#ffffff');
const editScale = ref(1);
const editOffsetX = ref(0);
const editOffsetY = ref(0);
const editSource = ref<ThumbEditSource | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const replaceInput = ref<HTMLInputElement | null>(null);

let countdownTimer: number | undefined;
let cropDrag:
  | {
      active: boolean;
      pointerId: number;
      startX: number;
      startY: number;
      originX: number;
      originY: number;
      frameScale: number;
    }
  | undefined;

const activeItems = computed(() => items.value.filter((item) => !item.deleted && item.submitStatus !== 'submitted'));
const submittedCount = computed(() => items.value.filter((item) => item.submitStatus === 'submitted').length);
const unrecognizedCount = computed(() => activeItems.value.filter((item) => !item.dressTypeId).length);
const canGenerateThumbs = computed(() => activeItems.value.length > 0 && unrecognizedCount.value === 0);
const canSubmit = computed(() => activeItems.value.length > 0 && activeItems.value.every((item) => item.thumbBlob && item.dressTypeId));
const currentEditItem = computed(() => items.value.find((item) => item.clientItemId === editingItemId.value));
const selectedTypeItem = computed(() => items.value.find((item) => item.clientItemId === typePickerItemId.value));
const editScaleLabel = computed(() => `${Math.round(editScale.value * 100)}%`);
const psdMaxSourceMb = computed(() => settingStore.psdImportConfig.max_source_mb || 150);
const psdMaxLayerCount = computed(() => settingStore.psdImportConfig.max_layer_count || 300);
const editImageStyle = computed(() => {
  const source = editSource.value;
  if (!source) return {};
  const sourceScale = Math.min(270 / source.bounds.width, 270 / source.bounds.height) * editScale.value;
  const contentCenterX = (source.bounds.x + source.bounds.width / 2) * sourceScale;
  const contentCenterY = (source.bounds.y + source.bounds.height / 2) * sourceScale;
  return {
    width: `${(source.canvas.width * sourceScale / 300) * 100}%`,
    height: `${(source.canvas.height * sourceScale / 300) * 100}%`,
    left: `${((150 - contentCenterX + editOffsetX.value) / 300) * 100}%`,
    top: `${((150 - contentCenterY + editOffsetY.value) / 300) * 100}%`,
  };
});
const sortedItems = computed(() => {
  return [...activeItems.value].sort((a, b) => {
    if (!a.dressTypeId && b.dressTypeId) return -1;
    if (a.dressTypeId && !b.dressTypeId) return 1;
    return a.order - b.order;
  });
});

const showMessage = (message: string) => {
  flowError.value = message;
  window.setTimeout(() => {
    if (flowError.value === message) flowError.value = '';
  }, 3000);
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error && 'message' in error) return String((error as { message?: unknown }).message || '操作失败');
  return String(error || '操作失败');
};

const startCountdown = () => {
  window.clearInterval(countdownTimer);
  countdown.value = 60;
  countdownTimer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) window.clearInterval(countdownTimer);
  }, 1000);
};

const normalizePhone = () => {
  phone.value = phone.value.replace(/\D/g, '').slice(0, 11);
  return phone.value;
};

const sendSms = async () => {
  loginError.value = '';
  const normalizedPhone = normalizePhone();
  if (!/^1[3456789]\d{9}$/.test(normalizedPhone)) {
    loginError.value = '手机号格式有误';
    return;
  }
  try {
    const result = await getSmsCode({ type: 'login', phone: normalizedPhone });
    smsId.value = String((result as { id?: string }).id || '');
    startCountdown();
  } catch (error) {
    loginError.value = getErrorMessage(error);
  }
};

const loadCreatorContext = async () => {
  pageLoading.value = true;
  try {
    await Promise.all([
      userStore.loadUserInfo(),
      settingStore.getSettingInfo(),
      getDressTypeList().then((types) => {
        dressTypes.value = types;
      }),
    ]);
  } catch (error) {
    showMessage(getErrorMessage(error));
  } finally {
    pageLoading.value = false;
  }
};

const handleLogin = async () => {
  loginError.value = '';
  const normalizedPhone = normalizePhone();
  const normalizedCode = smsCode.value.trim();
  const canUseDevCode = import.meta.env.MODE !== 'production' && normalizedCode === '8888';
  if (!normalizedPhone || !normalizedCode) {
    loginError.value = '请输入手机号和验证码';
    return;
  }
  if (!smsId.value && !canUseDevCode) {
    loginError.value = '请先获取验证码';
    return;
  }
  loginLoading.value = true;
  try {
    const result = await loginByPhone({
      phone: normalizedPhone,
      sms_code: normalizedCode,
      sms_id: smsId.value,
    });
    authStore.setToken(result.token);
    await loadCreatorContext();
  } catch (error) {
    loginError.value = getErrorMessage(error);
  } finally {
    loginLoading.value = false;
  }
};

const resetCreatorState = () => {
  step.value = 'upload';
  clearEditSource();
  editingItemId.value = '';
  pendingDeleteItemId.value = '';
  revokeParsedItemUrls(items.value);
  items.value = [];
  progress.value = null;
  flowError.value = '';
};

const handleLogout = () => {
  authStore.clearToken();
  userStore.clearUserInfo();
  resetCreatorState();
};

const openFilePicker = async () => {
  const showOpenFilePicker = (window as FilePickerWindow).showOpenFilePicker;
  if (window.isSecureContext && showOpenFilePicker) {
    try {
      const [handle] = await showOpenFilePicker({
        multiple: false,
        excludeAcceptAllOption: true,
        types: psdPickerTypes,
      });
      await handleFile(await handle.getFile());
      return;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  }
  fileInput.value?.click();
};

const handleFile = async (file?: File) => {
  if (!file) return;
  flowError.value = '';
  progress.value = null;
  parseLoading.value = true;
  try {
    revokeParsedItemUrls(items.value);
    const result = await parsePsdFile({
      file,
      dressTypes: dressTypes.value,
      maxSourceMb: psdMaxSourceMb.value,
      maxLayerCount: psdMaxLayerCount.value,
      onProgress: (nextProgress) => {
        progress.value = nextProgress;
      },
    });
    items.value = result.items;
    step.value = 'preview';
  } catch (error) {
    showMessage(getErrorMessage(error));
  } finally {
    parseLoading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

const handleFileInput = (event: Event) => {
  const input = event.target as HTMLInputElement;
  handleFile(input.files?.[0]);
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  handleFile(event.dataTransfer?.files?.[0]);
};

const requestDeleteItem = (item: ParsedDressItem) => {
  pendingDeleteItemId.value = item.clientItemId;
};

const confirmDeleteItem = () => {
  const item = items.value.find((candidate) => candidate.clientItemId === pendingDeleteItemId.value);
  pendingDeleteItemId.value = '';
  if (!item) return;
  item.deleted = true;
  if (activeItems.value.length === 0) step.value = 'upload';
};

const openImagePreview = (item: ParsedDressItem) => {
  imagePreview.value = {
    url: item.originUrl,
    title: item.layerName,
  };
};

const requestBack = () => {
  if (step.value === 'preview') {
    showPreviewBackConfirm.value = true;
    return;
  }
  if (step.value === 'confirm') {
    step.value = 'preview';
  }
};

const confirmBackToUpload = () => {
  showPreviewBackConfirm.value = false;
  revokeParsedItemUrls(items.value);
  items.value = [];
  progress.value = null;
  step.value = 'upload';
};

const finishSubmitSuccess = () => {
  showSubmitSuccess.value = false;
  revokeParsedItemUrls(items.value);
  items.value = [];
  progress.value = null;
  step.value = 'upload';
};

const openTypePicker = (item: ParsedDressItem) => {
  typePickerItemId.value = item.clientItemId;
};

const selectDressType = (type: API.DressType) => {
  if (!selectedTypeItem.value) return;
  selectedTypeItem.value.dressTypeId = type.id;
  selectedTypeItem.value.dressTypeName = type.name;
  selectedTypeItem.value.recognized = true;
  typePickerItemId.value = '';
};

const applyThumbToItem = (item: ParsedDressItem, blob: Blob) => {
  if (item.thumbUrl) URL.revokeObjectURL(item.thumbUrl);
  item.thumbBlob = blob;
  item.thumbUrl = URL.createObjectURL(blob);
};

const clearEditSource = () => {
  if (editSource.value?.ownedUrl) URL.revokeObjectURL(editSource.value.url);
  editSource.value = null;
};

const closeEditor = () => {
  clearEditSource();
  editingItemId.value = '';
};

const readImageSize = async (url: string) => {
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  await image.decode();
  return {
    width: image.naturalWidth,
    height: image.naturalHeight,
  };
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const updateEditScale = (nextScale: number) => {
  editScale.value = clamp(Number(nextScale.toFixed(2)), 0.5, 2.5);
};

const changeEditScale = (delta: number) => {
  updateEditScale(editScale.value + delta);
};

const resetEditorTransform = () => {
  editScale.value = 1;
  editOffsetX.value = 0;
  editOffsetY.value = 0;
};

const handleEditorWheel = (event: WheelEvent) => {
  event.preventDefault();
  changeEditScale(event.deltaY < 0 ? 0.05 : -0.05);
};

const beginEditorDrag = (event: PointerEvent) => {
  if (event.button !== 0) return;
  const target = event.currentTarget as HTMLElement;
  const rect = target.getBoundingClientRect();
  cropDrag = {
    active: true,
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    originX: editOffsetX.value,
    originY: editOffsetY.value,
    frameScale: 300 / Math.max(rect.width, 1),
  };
  target.setPointerCapture?.(event.pointerId);
};

const moveEditorDrag = (event: PointerEvent) => {
  if (!cropDrag?.active || cropDrag.pointerId !== event.pointerId) return;
  editOffsetX.value = clamp(cropDrag.originX + (event.clientX - cropDrag.startX) * cropDrag.frameScale, -150, 150);
  editOffsetY.value = clamp(cropDrag.originY + (event.clientY - cropDrag.startY) * cropDrag.frameScale, -150, 150);
};

const endEditorDrag = (event: PointerEvent) => {
  if (cropDrag?.pointerId === event.pointerId) cropDrag = undefined;
};

const generateAllThumbs = async () => {
  if (!canGenerateThumbs.value) return;
  parseLoading.value = true;
  try {
    for (const item of activeItems.value) {
      const thumb = await generateThumbnail(item.originBlob, item.contentBounds, item.canvas);
      applyThumbToItem(item, thumb);
      await new Promise((resolve) => window.setTimeout(resolve, 0));
    }
    step.value = 'confirm';
  } catch (error) {
    showMessage(getErrorMessage(error));
  } finally {
    parseLoading.value = false;
  }
};

const openEditor = (item: ParsedDressItem) => {
  clearEditSource();
  editingItemId.value = item.clientItemId;
  editSource.value = {
    blob: item.originBlob,
    url: item.originUrl,
    bounds: item.contentBounds,
    canvas: item.canvas,
    ownedUrl: false,
  };
  editBackground.value = '#ffffff';
  resetEditorTransform();
};

const confirmEditor = async () => {
  const item = currentEditItem.value;
  const source = editSource.value;
  if (!item || !source) return;
  try {
    const thumb = await generateThumbnail(source.blob, source.bounds, source.canvas, editBackground.value, editScale.value, {
      offsetX: editOffsetX.value,
      offsetY: editOffsetY.value,
    });
    applyThumbToItem(item, thumb);
    closeEditor();
  } catch (error) {
    showMessage(getErrorMessage(error));
  }
};

const triggerReplaceThumb = () => {
  replaceInput.value?.click();
};

const replaceThumb = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  const item = currentEditItem.value;
  if (!file || !item) return;
  const url = URL.createObjectURL(file);
  try {
    const size = await readImageSize(url);
    clearEditSource();
    editSource.value = {
      blob: file,
      url,
      bounds: { x: 0, y: 0, width: size.width, height: size.height },
      canvas: size,
      ownedUrl: true,
    };
    resetEditorTransform();
  } catch (error) {
    URL.revokeObjectURL(url);
    showMessage(getErrorMessage(error));
  } finally {
    input.value = '';
  }
};

let creatorDisposed = false;
const uploadItemImage = async (item: ParsedDressItem, type: 'origin' | 'thumb', token: string) => {
  const blob = type === 'origin'
    ? await compressPngUnderLimit(item.originBlob, dressOriginMaxBytes, '装扮图最大支持2M')
    : item.thumbBlob
      ? await compressPngUnderLimit(item.thumbBlob, dressThumbMaxBytes, '缩略图最大支持200KB')
      : null;
  if (!blob) throw new Error('缩略图未生成');
  const scene = type === 'origin' ? 'dress_origin' : 'dress_thumb';
  const fileName = type === 'origin' ? item.fileName : `${item.clientItemId}_thumb.png`;
  const hint = type === 'origin' ? await analyzeDressPng(blob) : undefined;
  if (creatorDisposed || authStore.token !== token) throw new Error('账号已变化，请重新进入创作中心');
  return { url: await uploadImage(blob, scene, fileName, item.clientItemId), hint };
};

const submitBatch = async () => {
  if (!canSubmit.value || submitLoading.value) return;
  submitLoading.value = true;
  const token = authStore.token;
  try {
    const payloadItems: API.BatchCreateDressItem[] = [];
    for (const item of activeItems.value) {
      if (creatorDisposed || authStore.token !== token) return;
      item.submitStatus = 'submitting';
      item.submitMessage = '';
      try {
        const originUrl = await uploadItemImage(item, 'origin', token);
        const thumbUrl = await uploadItemImage(item, 'thumb', token);
        payloadItems.push({
          client_item_id: item.clientItemId,
          origin_img_url: originUrl.url,
          thumb_img_url: thumbUrl.url,
          ...(originUrl.hint ? { color_hint: originUrl.hint } : {}),
          dress_type_id: item.dressTypeId,
          creative_type: item.creativeType,
          fit_type: item.fitType,
        });
      } catch (error) {
        item.submitStatus = 'failed';
        item.submitMessage = getErrorMessage(error);
      }
    }

    if (payloadItems.length === 0) {
      showMessage('图片上传失败，请重试');
      return;
    }

    if (creatorDisposed || authStore.token !== token) return;
    const result = await batchAddDress({ items: payloadItems });
    if (creatorDisposed || authStore.token !== token) return;
    result.results.forEach((entry) => {
      const item = items.value.find((candidate) => candidate.clientItemId === entry.client_item_id);
      if (!item) return;
      item.submitStatus = entry.success ? 'submitted' : 'failed';
      item.submitMessage = entry.success ? '提交成功' : entry.message || '提交失败';
    });

    const failedItems = activeItems.value.filter((item) => item.submitStatus === 'failed').length;
    const failedCount = Math.max(result.failed_count, failedItems);
    if (failedCount > 0) {
      showMessage(`${result.success_count} 个提交成功，${failedCount} 个提交失败，可继续重试`);
      return;
    }
    showSubmitSuccess.value = true;
  } catch (error) {
    const message = getErrorMessage(error);
    activeItems.value.forEach((item) => {
      if (item.submitStatus === 'submitting') {
        item.submitStatus = 'failed';
        item.submitMessage = message;
      }
    });
    showMessage(message);
  } finally {
    submitLoading.value = false;
  }
};

const downloadTemplate = () => {
  const url = settingStore.psdImportConfig?.example_url || settingStore.settingInfo?.example_url;
  if (!url) {
    showMessage('暂无人模文件下载地址');
    return;
  }
  window.open(url, '_blank');
};

onMounted(() => {
  settingStore.getPsdImportConfig();
  if (authStore.token) loadCreatorContext();
});

watch(
  () => authStore.token,
  (token) => {
    if (!token) resetCreatorState();
  }
);

onBeforeUnmount(() => {
  creatorDisposed = true;
  window.clearInterval(countdownTimer);
  clearEditSource();
  revokeParsedItemUrls(items.value);
});
</script>

<template>
  <div class="creator">
    <div v-if="flowError" class="creator__toast">{{ flowError }}</div>

    <section v-if="!authStore.token" class="login-panel">
      <h1>登录后进入创作者中心</h1>
      <p>请先登录萌屿账号，登录成功后再根据账号身份进入对应页面。</p>
      <input v-model="phone" type="tel" inputmode="numeric" maxlength="11" placeholder="手机号" @input="normalizePhone" />
      <div class="login-panel__code">
        <input v-model="smsCode" maxlength="4" placeholder="验证码" @keyup.enter="handleLogin" />
        <button :disabled="countdown > 0" @click="sendSms">{{ countdown > 0 ? `${countdown}s后重新获取` : '获取验证码' }}</button>
      </div>
      <button class="primary-btn" :disabled="loginLoading" @click="handleLogin">{{ loginLoading ? '登录中...' : '登录' }}</button>
      <span v-if="loginError" class="error-text">{{ loginError }}</span>
    </section>

    <section v-else-if="pageLoading" class="state-card">正在读取账号信息...</section>

    <section v-else-if="!userStore.isCreator" class="state-card">
      <h1>您还不是创作者哦</h1>
      <p>请在萌屿APP中申请开通萌铺后才能使用该功能。</p>
      <button @click="handleLogout">切换账号</button>
    </section>

    <template v-else>
      <div class="creator__headline">
        <div class="creator__headline-main">
          <h1 v-if="step === 'upload'">PSD 快捷上传</h1>
          <h1 v-else-if="step === 'preview'">识别预览</h1>
          <h1 v-else>最终确认提交</h1>
          <button v-if="step !== 'upload'" class="back-step-btn" @click="requestBack">返回上一步</button>
        </div>
        <p v-if="step === 'upload'">拖拽 PSD 文件到页面中，系统会先校验格式和 sRGB，再读取可见图层。</p>
        <p v-else-if="step === 'preview'">上传通过后直接展示识别结果，未识别项置顶；全部选择部位后生成缩略图。</p>
        <p v-else>缩略图生成后，逐项确认类型和适配人模，完成后提交审核。</p>
      </div>

      <div v-if="step === 'upload'" class="upload-layout">
        <aside class="guide-card">
          <h2>开始前</h2>
          <button class="guide-action guide-action--pink" @click="downloadTemplate">
            <strong>下载人模文件</strong>
            <span>基于官方模板绘制，避免尺寸和位置偏差。</span>
          </button>
          <button class="guide-action guide-action--purple" @click="showUsageGuide = true">
            <strong>使用说明</strong>
            <span>查看命名规则、图层状态、识别范围。</span>
          </button>
        </aside>

        <main class="upload-card">
          <h2>上传 PSD 文件</h2>
          <div class="drop-zone" @click="openFilePicker" @drop="handleDrop" @dragover.prevent>
            <div class="psd-icon">PSD</div>
            <strong>{{ parseLoading ? progress?.message || '正在解析...' : '拖拽 PSD 到这里，或点击选择文件' }}</strong>
          </div>
          <input ref="fileInput" type="file" :accept="psdFileAccept" hidden @change="handleFileInput" />
          <ul class="upload-rules">
            <li>仅支持 .psd 文件</li>
            <li>隐藏图层不会提取</li>
            <li>文件大小≤{{ psdMaxSourceMb }}M，图层数量≤{{ psdMaxLayerCount }}个</li>
          </ul>
        </main>
      </div>

      <div v-else-if="step === 'preview'" class="work-layout">
        <main class="list-card">
          <div class="list-card__title">
            <h2>识别出的装扮项</h2>
            <span>未识别优先展示</span>
          </div>
          <div class="list-card__scroll">
            <article v-for="item in sortedItems" :key="item.clientItemId" class="dress-row" :class="{ 'dress-row--warn': !item.dressTypeId }">
              <img class="previewable-img" :src="item.originUrl" alt="" @click="openImagePreview(item)" />
              <div class="dress-row__info">
                <strong>{{ item.layerName }}</strong>
                <span>可见图层 · {{ item.groupPath.length || 0 }} 个分组</span>
              </div>
              <button class="pill" :class="{ 'pill--warn': !item.dressTypeId }" @click="openTypePicker(item)">
                {{ item.dressTypeName || '未识别' }}
              </button>
              <button class="text-btn" @click="requestDeleteItem(item)">删除</button>
            </article>
          </div>
        </main>

        <aside class="summary-card">
          <h2>本次识别</h2>
          <strong>{{ activeItems.length }}</strong>
          <span>装扮项</span>
          <strong class="pink">{{ unrecognizedCount }}</strong>
          <span>未识别</span>
          <button class="primary-btn" :disabled="!canGenerateThumbs || parseLoading" @click="generateAllThumbs">
            {{ canGenerateThumbs ? '下一步，生成缩略图' : '请先完成未识别部位' }}
          </button>
          <p>全部有部位后按钮变为：下一步，生成缩略图</p>
        </aside>
      </div>

      <div v-else class="work-layout">
        <main class="list-card">
          <div class="list-card__title">
            <h2>上传装扮</h2>
            <span>{{ activeItems.length }} 项待提交，{{ submittedCount }} 项已成功</span>
          </div>
          <div class="confirm-header">
            <span>装扮图</span>
            <span>缩略图</span>
            <span></span>
            <span>选择类型</span>
            <span>适配人模</span>
          </div>
          <div class="list-card__scroll">
            <article v-for="item in activeItems" :key="item.clientItemId" class="confirm-row">
              <img class="previewable-img" :src="item.originUrl" alt="" @click="openImagePreview(item)" />
              <div class="thumb-box" @click="openEditor(item)">
                <img v-if="item.thumbUrl" :src="item.thumbUrl" alt="" />
                <span>修改</span>
              </div>
              <div>
                <strong>{{ item.dressTypeName }}</strong>
                <p>{{ item.layerName }}</p>
                <small v-if="item.submitMessage">{{ item.submitMessage }}</small>
              </div>
              <div class="segmented segmented--type">
                <button :class="{ active: item.creativeType === 1 }" @click="item.creativeType = 1">原创</button>
                <button :class="{ active: item.creativeType === 2 }" @click="item.creativeType = 2">其他</button>
              </div>
              <div class="segmented">
                <button :class="{ active: item.fitType === 0 }" @click="item.fitType = 0">官方人模</button>
                <button :class="{ active: item.fitType === 1 }" @click="item.fitType = 1">其他人模</button>
              </div>
            </article>
          </div>
        </main>

        <aside class="summary-card">
          <h2>提交前检查</h2>
          <p>✓ {{ activeItems.filter((item) => item.thumbBlob).length }} 个缩略图已生成</p>
          <p>✓ 类型已全部选择</p>
          <p>✓ 适配人模已全部选择</p>
          <p>✓ 等待提交审核</p>
          <button class="primary-btn" :disabled="!canSubmit || submitLoading" @click="submitBatch">
            {{ submitLoading ? '提交中...' : `提交 ${activeItems.length} 个装扮` }}
          </button>
          <p>提交后进入后台审核</p>
        </aside>
      </div>
    </template>

    <div v-if="submitLoading" class="modal-mask submit-lock-mask">
      <div class="submit-lock-modal">
        <div class="submit-lock-modal__spinner"></div>
        <h2>正在提交</h2>
        <p>正在上传图片并提交审核，请勿刷新页面或重复操作。</p>
      </div>
    </div>

    <div v-if="typePickerItemId" class="modal-mask" @click.self="typePickerItemId = ''">
      <div class="type-modal">
        <div class="type-modal__header">
          <h3>选择部位</h3>
          <button type="button" @click="typePickerItemId = ''">关闭</button>
        </div>
        <div class="type-grid">
          <button v-for="type in dressTypes" :key="type.id" :class="{ active: selectedTypeItem?.dressTypeId === type.id }" @click="selectDressType(type)">
            <img v-if="type.icon_select || type.icon_unselect" :src="type.icon_select || type.icon_unselect" alt="" />
            <span>{{ type.name }}</span>
          </button>
        </div>
      </div>
    </div>

    <div v-if="pendingDeleteItemId" class="modal-mask" @click.self="pendingDeleteItemId = ''">
      <div class="confirm-modal">
        <h2>提示</h2>
        <p>移除后，该装扮将不会被上传。<br />此操作不会影响源文件中的内容。</p>
        <div class="confirm-modal__actions">
          <button class="secondary-btn" @click="pendingDeleteItemId = ''">取消</button>
          <button class="primary-btn" @click="confirmDeleteItem">确认移除</button>
        </div>
      </div>
    </div>

    <div v-if="showUsageGuide" class="modal-mask" @click.self="showUsageGuide = false">
      <div class="guide-modal">
        <h2>使用说明</h2>
        <ol>
          <li>
            <strong>1.先下载人模文件</strong>
            <span>请基于官方提供的模板文件绘制，避免尺寸和位置偏差。</span>
          </li>
          <li>
            <strong>2.单个装扮需合并图层</strong>
            <span>绘制完成后，单个部件内的图层需要合并。</span>
          </li>
          <li>
            <strong>3.保持图层可见</strong>
            <span>隐藏状态下的图层或分组不会被识别，也不会生成装扮图。</span>
          </li>
          <li>
            <strong>4.将装扮放入文件所提供的分类名称内</strong>
            <span>分组名为“眼睛”时，上传后会默认选择到眼睛分区。若不在规则内，系统不会自动识别，需要上传后手动选择分类。</span>
          </li>
          <li>
            <strong>5.颜色配置文件要求</strong>
            <span>PSD 文件需使用标准 sRGB 色彩空间，非 sRGB 可能导致提取后颜色显示异常。</span>
          </li>
        </ol>
        <button class="secondary-btn guide-modal__close" @click="showUsageGuide = false">关闭</button>
      </div>
    </div>

    <div v-if="showPreviewBackConfirm" class="modal-mask" @click.self="showPreviewBackConfirm = false">
      <div class="confirm-modal">
        <h2>提示</h2>
        <p>返回后当前页面数据不会保存，确认返回吗</p>
        <div class="confirm-modal__actions">
          <button class="secondary-btn" @click="showPreviewBackConfirm = false">取消</button>
          <button class="primary-btn" @click="confirmBackToUpload">确认返回</button>
        </div>
      </div>
    </div>

    <div v-if="showSubmitSuccess" class="modal-mask">
      <div class="confirm-modal submit-success-modal">
        <h2>提交成功</h2>
        <p>装扮已提交审核，可在萌屿APP中查看审核进度。</p>
        <div class="confirm-modal__actions">
          <button class="primary-btn" @click="finishSubmitSuccess">返回上传页</button>
        </div>
      </div>
    </div>

    <div v-if="imagePreview" class="modal-mask image-preview-mask" @click.self="imagePreview = null">
      <div class="image-preview-modal">
        <div class="image-preview-modal__header">
          <strong>{{ imagePreview.title }}</strong>
          <button @click="imagePreview = null">关闭</button>
        </div>
        <img :src="imagePreview.url" alt="" />
      </div>
    </div>

    <div v-if="currentEditItem && editSource" class="modal-mask" @click.self="closeEditor">
      <div class="thumb-modal">
        <h2>编辑缩略图</h2>
        <div class="thumb-modal__body">
          <div class="crop-stage">
            <div
              class="crop-frame"
              :style="{ background: editBackground }"
              @pointerdown="beginEditorDrag"
              @pointermove="moveEditorDrag"
              @pointerup="endEditorDrag"
              @pointercancel="endEditorDrag"
              @wheel="handleEditorWheel"
            >
              <img class="crop-image" :src="editSource.url" alt="" :style="editImageStyle" draggable="false" />
              <span class="crop-frame__hint">拖动调整位置 · 滚轮缩放</span>
            </div>
          </div>
          <aside>
            <h3>缩略图预览</h3>
            <div class="preview-square" :style="{ background: editBackground }">
              <img :src="editSource.url" alt="" :style="editImageStyle" draggable="false" />
            </div>
            <div class="scale-header">
              <label>缩放</label>
              <span>{{ editScaleLabel }}</span>
            </div>
            <div class="scale-control">
              <button @click="changeEditScale(-0.05)">-</button>
              <input v-model.number="editScale" type="range" min="0.5" max="2.5" step="0.01" />
              <button @click="changeEditScale(0.05)">+</button>
            </div>
            <label>选择底色</label>
            <div class="color-row">
              <button class="color-row__white" :class="{ active: editBackground === '#ffffff' }" style="background:#fff" @click="editBackground = '#ffffff'"></button>
              <button :class="{ active: editBackground === '#999999' }" style="background:#999" @click="editBackground = '#999999'"></button>
              <button :class="{ active: editBackground === '#333333' }" style="background:#333" @click="editBackground = '#333333'"></button>
            </div>
            <button class="secondary-btn" @click="resetEditorTransform">重置位置</button>
            <button class="secondary-btn" @click="triggerReplaceThumb">选择其他图片</button>
            <input ref="replaceInput" type="file" accept="image/*" hidden @change="replaceThumb" />
            <div class="thumb-modal__actions">
              <button class="secondary-btn" @click="closeEditor">取消</button>
              <button class="primary-btn" @click="confirmEditor">确认</button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.creator {
  height: 100%;
  padding: 58px 168px 80px;
  box-sizing: border-box;
  color: #1f1d29;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.creator__toast {
  position: fixed;
  top: 100px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(31, 29, 41, 0.88);
  color: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  z-index: 30;
}

.creator__headline {
  flex: 0 0 auto;
  margin-bottom: 32px;

  h1 {
    font-size: 34px;
    font-weight: 600;
    margin-bottom: 8px;
  }

  p {
    font-size: 16px;
    color: #74707f;
  }
}

.creator__headline-main {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-step-btn {
  height: 38px;
  padding: 0 18px;
  border: 1px solid #e5d7e7;
  border-radius: 999px;
  background: #fff;
  color: #6f6576;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: #ff76a8;
    color: #fb5a9b;
  }
}

.login-panel,
.state-card {
  width: 500px;
  margin: 110px auto 0;
  padding: 58px 42px 48px;
  background: #fff;
  border: 1px solid #eaddea;
  border-radius: 8px;
  box-shadow: 0 22px 60px rgba(118, 69, 111, 0.12);

  h1 {
    font-size: 32px;
    font-weight: 500;
    margin-bottom: 20px;
  }

  p {
    color: #74707f;
    line-height: 1.7;
    margin-bottom: 22px;
  }

  input {
    width: 100%;
    height: 52px;
    padding: 0 18px;
    border: 1px solid #e8dae8;
    border-radius: 6px;
    box-sizing: border-box;
    font-size: 16px;
    margin-bottom: 16px;
  }
}

.login-panel__code {
  display: grid;
  grid-template-columns: 1fr 140px;
  gap: 14px;

  button {
    height: 52px;
    border: 2px solid #17151f;
    border-radius: 6px;
    background: #fff;
    cursor: pointer;
  }
}

.primary-btn,
.secondary-btn {
  height: 50px;
  border-radius: 8px;
  border: none;
  padding: 0 30px;
  font-size: 16px;
  cursor: pointer;
  white-space: nowrap;
}

.primary-btn {
  color: #fff;
  background: #fb5a9b;
  box-shadow: 5px 5px 0 #050505;

  &:disabled {
    background: #e9e0ea;
    box-shadow: none;
    color: #9d94a3;
    cursor: not-allowed;
  }
}

.secondary-btn {
  color: #6f6576;
  border: 1px solid #e3d5e5;
  background: #fff;
}

.error-text {
  display: block;
  margin-top: 12px;
  color: #fb5a9b;
}

.upload-layout,
.work-layout {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 40px;
  align-items: start;
}

.work-layout {
  grid-template-columns: 1fr 310px;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  align-items: stretch;
}

.guide-card,
.upload-card,
.list-card,
.summary-card {
  background: #fff;
  border: 1px solid #eaddea;
  border-radius: 8px;
  box-shadow: 0 20px 50px rgba(118, 69, 111, 0.08);
}

.guide-card {
  padding: 32px 28px;

  h2 {
    font-size: 28px;
    margin-bottom: 26px;
  }
}

.guide-action {
  display: block;
  width: 100%;
  min-height: 112px;
  padding: 20px;
  text-align: left;
  border-radius: 8px;
  margin-bottom: 20px;
  text-decoration: none;
  box-sizing: border-box;
  cursor: pointer;

  strong,
  span {
    display: block;
  }

  strong {
    color: #17151f;
    font-size: 20px;
    margin-bottom: 12px;
  }

  span {
    color: #746b78;
    line-height: 1.5;
  }
}

.guide-action--pink {
  border: 1px solid #ff6ca3;
  background: #ffeaf3;
}

.guide-action--purple {
  border: 1px solid #c7a2ff;
  background: #f1e9ff;
}

.upload-card {
  padding: 32px;

  h2 {
    font-size: 30px;
    margin-bottom: 28px;
  }

  ul {
    margin-top: 28px;
    list-style: none;
    color: #6f6576;
    line-height: 2;
  }
}

.upload-rules {
  display: grid;
  gap: 12px;
  padding: 0;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    color: #746b78;
    font-size: 18px;
    line-height: 1.35;
  }

  li::before {
    content: '✓';
    width: 28px;
    height: 28px;
    border: 2px solid #7ee3d7;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: #31c7b8;
    background: #e9fffb;
    font-size: 18px;
    font-weight: 700;
  }
}

.drop-zone {
  height: 240px;
  border: 2px solid #c799ff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 14px;
  background: #fbf8ff;
  cursor: pointer;

  strong {
    font-size: 22px;
  }
}

.psd-icon {
  width: 82px;
  height: 98px;
  border: 2px solid #c799ff;
  border-radius: 8px;
  color: #b276ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.list-card {
  padding: 30px;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.list-card__title {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 28px;
  margin-bottom: 24px;

  h2 {
    font-size: 28px;
  }

  span {
    border: 1px solid #ff76a8;
    color: #fb5a9b;
    border-radius: 999px;
    padding: 8px 20px;
  }
}

.list-card__scroll {
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
}

.list-card__scroll::-webkit-scrollbar {
  width: 6px;
}

.list-card__scroll::-webkit-scrollbar-thumb {
  background: #eaddea;
  border-radius: 999px;
}

.list-card__scroll::-webkit-scrollbar-track {
  background: transparent;
}

.dress-row,
.confirm-row {
  display: grid;
  align-items: center;
  gap: 20px;
  min-height: 82px;
  padding: 12px 16px;
  border: 1px solid #eaddea;
  border-radius: 8px;
  margin-bottom: 16px;

  > img {
    width: 58px;
    height: 58px;
    object-fit: contain;
    background: #fff0f7;
    border-radius: 6px;
  }
}

.dress-row {
  grid-template-columns: 72px 1fr 120px 60px;
}

.dress-row--warn {
  border-color: #ff76a8;
  background: #fff8fb;
}

.dress-row__info {
  strong,
  span {
    display: block;
  }

  strong {
    font-size: 18px;
    margin-bottom: 8px;
  }

  span {
    color: #7e7586;
  }
}

.pill {
  border: 1px solid #48d2c7;
  color: #1bbeb2;
  background: #effffc;
  border-radius: 999px;
  padding: 9px 18px;
  cursor: pointer;
}

.pill--warn {
  border-color: #ff76a8;
  color: #fb5a9b;
  background: #fff5fa;
}

.text-btn {
  border: none;
  background: transparent;
  color: #a098aa;
  cursor: pointer;
  white-space: nowrap;
}

.summary-card {
  padding: 30px;
  max-height: 100%;
  overflow: auto;

  h2 {
    font-size: 26px;
    margin-bottom: 28px;
  }

  strong {
    display: inline-block;
    font-size: 54px;
    margin: 10px 28px 20px 0;
  }

  span,
  p {
    color: #746b78;
    line-height: 1.7;
  }

  .primary-btn {
    width: 100%;
    margin: 38px 0 18px;
  }
}

.pink {
  color: #fb5a9b;
}

.confirm-row {
  grid-template-columns: 70px 70px minmax(160px, 1fr) 158px 204px;
  gap: 18px;
  min-height: 76px;
  padding: 10px 14px;
  margin-bottom: 12px;

  p,
  small {
    color: #817987;
  }

  > img {
    width: 58px;
    height: 58px;
  }

  strong {
    display: block;
    font-size: 17px;
    line-height: 1.35;
    margin-bottom: 3px;
  }

  p {
    font-size: 15px;
    line-height: 1.35;
  }
}

.confirm-header {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: 70px 70px minmax(160px, 1fr) 158px 204px;
  gap: 18px;
  align-items: center;
  padding: 12px 14px 14px;
  margin: 2px 0 12px;
  border-bottom: 1px solid #eaddea;
  color: #8b8292;
  font-size: 15px;
  font-weight: 600;
  text-align: center;

  span:nth-child(3) {
    visibility: hidden;
  }
}

.thumb-box {
  width: 58px;
  height: 58px;
  border: 1px solid #e6d9e7;
  border-radius: 8px;
  position: relative;
  cursor: pointer;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  span {
    position: absolute;
    left: 0;
    right: 0;
    bottom: -18px;
    text-align: center;
    color: #fb5a9b;
    font-size: 14px;
  }
}

.previewable-img {
  cursor: zoom-in;
  transition: transform 0.16s ease;

  &:hover {
    transform: scale(1.04);
  }
}

.segmented {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  box-sizing: border-box;

  button {
    border: 1px solid #e2d5e4;
    background: #fff;
    border-radius: 999px;
    min-width: 64px;
    min-height: 36px;
    box-sizing: border-box;
    padding: 6px 16px;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.2;
    white-space: nowrap;

    &.active {
      border-color: #fb5a9b;
      background: #fb5a9b;
      color: #fff;
    }
  }
}

.segmented--type {
  position: relative;
  padding-right: 22px;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 0;
    width: 1px;
    height: 42px;
    transform: translateY(-50%);
    background: #eaddea;
  }
}

.modal-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(31, 29, 41, 0.18);
  display: flex;
  justify-content: center;
  align-items: center;
}

.confirm-modal {
  width: 420px;
  max-width: calc(100vw - 40px);
  background: #fff;
  border-radius: 12px;
  padding: 30px 28px;
  box-shadow: 0 18px 60px rgba(68, 43, 69, 0.18);
  text-align: center;

  h2 {
    font-size: 24px;
    margin-bottom: 18px;
  }

  p {
    color: #17151f;
    font-size: 17px;
    line-height: 1.7;
  }
}

.confirm-modal__actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 28px;

  button {
    min-width: 120px;
    white-space: nowrap;
  }
}

.submit-lock-mask {
  z-index: 70;
  cursor: wait;
}

.submit-lock-modal {
  width: 360px;
  max-width: calc(100vw - 40px);
  background: #fff;
  border-radius: 12px;
  padding: 34px 28px;
  box-shadow: 0 18px 60px rgba(68, 43, 69, 0.22);
  text-align: center;

  h2 {
    margin: 18px 0 10px;
    color: #17151f;
    font-size: 24px;
  }

  p {
    margin: 0;
    color: #746b78;
    font-size: 16px;
    line-height: 1.6;
  }
}

.submit-lock-modal__spinner {
  width: 42px;
  height: 42px;
  margin: 0 auto;
  border: 4px solid #ffe1ef;
  border-top-color: #fb5a9b;
  border-radius: 50%;
  animation: submit-spin 0.8s linear infinite;
}

@keyframes submit-spin {
  to {
    transform: rotate(360deg);
  }
}

.image-preview-mask {
  background: rgba(20, 18, 26, 0.72);
}

.image-preview-modal {
  width: min(860px, calc(100vw - 64px));
  max-height: calc(100vh - 64px);
  background: #fff;
  border-radius: 8px;
  padding: 18px;
  box-sizing: border-box;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  gap: 14px;

  img {
    max-width: 100%;
    max-height: calc(100vh - 150px);
    object-fit: contain;
    background: #fff0f7;
    border-radius: 6px;
  }
}

.image-preview-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  strong {
    font-size: 18px;
  }

  button {
    height: 34px;
    padding: 0 16px;
    border: 1px solid #e5d7e7;
    border-radius: 999px;
    background: #fff;
    cursor: pointer;
    white-space: nowrap;
  }
}

.type-modal,
.thumb-modal {
  background: #fff;
  border: 1px solid #ff98bf;
  border-radius: 8px;
  padding: 28px;
  box-shadow: 0 18px 60px rgba(68, 43, 69, 0.18);
}

.type-modal {
  width: 420px;
}

.type-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  h3 {
    margin: 0;
  }

  button {
    display: none;
    height: 32px;
    padding: 0 14px;
    border: 1px solid #e5d7e7;
    border-radius: 999px;
    background: #fff;
    color: #746b78;
    cursor: pointer;
    white-space: nowrap;
  }
}

.guide-modal {
  width: 760px;
  max-width: calc(100vw - 48px);
  background: #fff;
  border: 1px solid #eaddea;
  border-radius: 8px;
  padding: 48px;
  box-sizing: border-box;
  box-shadow: 0 24px 70px rgba(68, 43, 69, 0.2);

  h2 {
    text-align: center;
    font-size: 30px;
    font-weight: 600;
    margin-bottom: 44px;
  }

  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    margin-bottom: 28px;

    strong,
    span {
      display: block;
    }

    strong {
      color: #17151f;
      font-size: 22px;
      font-weight: 700;
      line-height: 1.4;
      margin-bottom: 8px;
    }

    span {
      color: #777180;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.55;
    }
  }
}

.guide-modal__close {
  display: block;
  min-width: 168px;
  margin: 46px auto 0;
  border: 2px solid #17151f;
  color: #17151f;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  margin-top: 18px;

  button {
    min-height: 62px;
    border: 1px solid #eee5ef;
    border-radius: 8px;
    background: #f7f3f8;
    cursor: pointer;
    color: #6f6576;

    &.active {
      border-color: #fb5a9b;
      background: #fff3f8;
    }
  }

  img {
    display: block;
    width: 30px;
    height: 30px;
    object-fit: contain;
    margin: 0 auto 4px;
  }
}

.thumb-modal {
  width: 1040px;
}

.thumb-modal__body {
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 32px;
  margin-top: 22px;
}

.crop-stage {
  height: 430px;
  background: #9d9d9d;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.crop-frame,
.preview-square {
  position: relative;
  overflow: hidden;

  img {
    position: absolute;
    object-fit: fill;
    max-width: none;
    max-height: none;
    user-select: none;
    pointer-events: none;
  }
}

.crop-frame {
  width: 340px;
  height: 340px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(31, 29, 41, 0.28), 0 10px 30px rgba(31, 29, 41, 0.2);
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
}

.crop-frame__hint {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(31, 29, 41, 0.62);
  color: #fff;
  font-size: 12px;
  white-space: nowrap;
}

.preview-square {
  width: 208px;
  height: 208px;
  margin: 20px 0;
  border: 1px solid #eaddea;
}

.scale-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 208px;
  margin-bottom: 10px;

  span {
    color: #fb5a9b;
    font-weight: 600;
  }
}

.scale-control {
  display: grid;
  grid-template-columns: 34px 1fr 34px;
  gap: 10px;
  align-items: center;
  width: 208px;
  margin-bottom: 18px;

  button {
    height: 34px;
    border: 1px solid #e3d5e5;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 18px;
  }

  input {
    width: 100%;
    accent-color: #fb5a9b;
  }
}

.color-row {
  display: flex;
  gap: 22px;
  margin: 14px 0 30px;

  button {
    width: 54px;
    height: 54px;
    border-radius: 50%;
    border: 2px solid transparent;
    cursor: pointer;

    &.color-row__white:not(.active) {
      border: 1px solid #fa7299;
    }

    &.active {
      border-color: #fa7299;
      box-shadow: 0 0 0 4px #ffe0ee;
    }
  }
}

.thumb-modal__actions {
  display: flex;
  gap: 20px;
  margin-top: 28px;
}

@media (min-width: 768px) and (max-width: 1199px) {
  .creator {
    padding: 2.625rem 2.25rem 3.25rem;
  }

  .creator__headline {
    margin-bottom: 1.5rem;

    h1 {
      font-size: 1.875rem;
    }
  }

  .back-step-btn {
    height: 2rem;
    padding: 0 0.875rem;
    border-width: 1px;
    border-color: #eaddea;
    font-size: 0.875rem;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 0.375rem 1rem rgba(118, 69, 111, 0.08);
  }

  .login-panel,
  .state-card {
    margin-top: 5.25rem;
  }

  .upload-layout {
    grid-template-columns: 18rem minmax(0, 1fr);
    gap: 1.5rem;
  }

  .work-layout {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 1rem;
  }

  .guide-card,
  .upload-card,
  .list-card,
  .summary-card {
    padding: 1.5rem;
  }

  .guide-card h2,
  .upload-card h2,
  .list-card__title h2 {
    font-size: 1.5rem;
  }

  .guide-action {
    min-height: 6.5rem;
    padding: 1.125rem;

    strong {
      font-size: 1.125rem;
    }
  }

  .drop-zone {
    height: 13.75rem;
  }

  .list-card__title {
    gap: 1rem;
    margin-bottom: 1.125rem;

    span {
      padding: 0.4375rem 1rem;
      white-space: nowrap;
    }
  }

  .dress-row {
    grid-template-columns: 3.75rem minmax(0, 1fr) 6.25rem 2.75rem;
    gap: 0.75rem;
    min-height: 4.75rem;
    padding: 0.625rem 0.75rem;
  }

  .dress-row > img {
    width: 3.375rem;
    height: 3.375rem;
  }

  .dress-row__info {
    strong {
      font-size: 1rem;
      margin-bottom: 0.25rem;
    }

    span {
      font-size: 0.875rem;
    }
  }

  .pill {
    padding: 0.4375rem 0.875rem;
    font-size: 0.875rem;
  }

  .text-btn {
    font-size: 0.875rem;
  }

  .confirm-header,
  .confirm-row {
    grid-template-columns: 3.875rem 3.875rem minmax(7.5rem, 1fr) 8.5rem 11rem;
    gap: 0.75rem;
  }

  .confirm-row {
    padding: 0.625rem 0.75rem;
    min-height: 4.5rem;
  }

  .confirm-row > img,
  .thumb-box {
    width: 3.375rem;
    height: 3.375rem;
  }

  .segmented {
    gap: 0.375rem;

    button {
      min-width: 3.625rem;
      min-height: 2.125rem;
      padding: 0.375rem 0.75rem;
      font-size: 0.8125rem;
    }
  }

  .segmented--type {
    padding-right: 1rem;
  }

  .summary-card {
    padding: 1.5rem 1.25rem;

    h2 {
      font-size: 1.375rem;
      margin-bottom: 1.25rem;
    }

    strong {
      font-size: 2.625rem;
      margin-right: 1.125rem;
    }

    .primary-btn {
      margin-top: 1.75rem;
      padding: 0 1.125rem;
    }
  }

  .confirm-modal,
  .submit-lock-modal {
    width: min(28rem, calc(100vw - 3rem));
    box-sizing: border-box;
    padding: 1.75rem;

    h2 {
      margin-bottom: 1rem;
      font-size: 1.35rem;
    }

    p {
      font-size: 1rem;
      line-height: 1.7;
    }
  }

  .confirm-modal__actions {
    gap: 0.75rem;
    margin-top: 1.5rem;

    button {
      min-width: 7rem;
      height: 2.625rem;
      padding: 0 1rem;
      font-size: 0.95rem;
    }
  }

  .type-modal {
    width: min(30rem, calc(100vw - 3rem));
    max-height: calc(100dvh - 4rem);
    box-sizing: border-box;
    overflow: auto;
    padding: 1.5rem;
  }

  .type-modal__header button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .type-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.625rem;
    margin-top: 1rem;

    button {
      min-height: 3.25rem;
      padding: 0.35rem 0.25rem;
      font-size: 0.875rem;
      line-height: 1.2;
    }

    img {
      width: 1.5rem;
      height: 1.5rem;
      margin-bottom: 0.2rem;
    }
  }

  .thumb-modal {
    width: calc(100vw - 3rem);
    max-height: calc(100vh - 3rem);
    box-sizing: border-box;
    overflow: auto;
  }

  .thumb-modal__body {
    grid-template-columns: minmax(0, 1fr) 15rem;
    gap: 1.5rem;
  }

  .crop-stage {
    height: 24.375rem;
  }

  .crop-frame {
    width: min(18rem, 100%);
    height: min(18rem, 100%);
  }

  .preview-square,
  .scale-header,
  .scale-control {
    width: 12.5rem;
  }

  .preview-square {
    height: 12.5rem;
  }
}

@media (min-width: 1024px) and (max-width: 1199px) {
  .work-layout {
    grid-template-columns: minmax(0, 1fr) 18rem;
    grid-template-rows: minmax(0, 1fr);
  }
}

@media (max-width: 767px) {
  .creator {
    padding: 1.5rem 1rem 1rem;
    height: calc(100dvh - 3.5rem);
    min-height: 0;
    overflow: hidden;
  }

  .creator__toast {
    top: 4.25rem;
    width: calc(100vw - 2rem);
    box-sizing: border-box;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    text-align: center;
  }

  .creator__headline {
    margin-bottom: 1rem;

    .creator__headline-main {
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.75rem;
    }

    h1 {
      font-size: 1.55rem;
      line-height: 1.25;
    }

    p {
      font-size: 0.95rem;
      line-height: 1.6;
    }
  }

  .back-step-btn {
    flex: 0 0 auto;
    height: 1.9rem;
    margin-top: 0.2rem;
    padding: 0 0.72rem;
    border-width: 1px;
    border-color: #eaddea;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.86);
    color: #746b78;
    box-shadow: 0 0.25rem 0.875rem rgba(118, 69, 111, 0.08);
    font-size: 0.78rem;
  }

  .login-panel,
  .state-card {
    width: auto;
    max-width: 30rem;
    max-height: calc(100dvh - 7rem);
    margin: 2rem auto 0;
    padding: 1.5rem;
    box-sizing: border-box;
    overflow-y: auto;

    h1 {
      font-size: 1.45rem;
      line-height: 1.35;
      margin-bottom: 0.85rem;
    }

    p {
      font-size: 0.95rem;
      line-height: 1.65;
      margin-bottom: 1.1rem;
    }

    input {
      height: 2.8rem;
      margin-bottom: 0.8rem;
      padding: 0 0.8rem;
      font-size: 0.95rem;
    }
  }

  .login-panel__code {
    grid-template-columns: 1fr;
    gap: 0.75rem;

    button {
      height: 2.8rem;
      font-size: 0.95rem;
      white-space: nowrap;
    }
  }

  .primary-btn,
  .secondary-btn {
    height: 2.65rem;
    padding: 0 1rem;
    font-size: 0.95rem;
    white-space: nowrap;
  }

  .upload-layout,
  .work-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 1rem;
    min-height: 0;
  }

  .upload-layout {
    flex: 1 1 auto;
    overflow-y: auto;
    padding-bottom: 1rem;
  }

  .work-layout {
    flex: 1 1 auto;
    grid-template-rows: minmax(0, 1fr) auto;
    overflow: hidden;
  }

  .guide-card,
  .upload-card,
  .list-card,
  .summary-card {
    width: 100%;
    box-sizing: border-box;
  }

  .guide-card,
  .upload-card,
  .list-card {
    padding: 1rem;
  }

  .list-card {
    min-height: 0;
    overflow: hidden;
  }

  .summary-card {
    position: static;
    padding: 1rem;
    max-height: none;
    overflow: visible;
  }

  .list-card__scroll {
    min-height: 0;
    overflow-y: auto;
    padding-right: 0;
  }

  .guide-card h2,
  .upload-card h2,
  .list-card__title h2,
  .summary-card h2 {
    font-size: 1.2rem;
  }

  .list-card__title {
    gap: 0.75rem;
    margin-bottom: 0.8rem;

    span {
      padding: 0.35rem 0.75rem;
      font-size: 0.85rem;
      white-space: nowrap;
    }
  }

  .guide-action {
    min-height: auto;
    padding: 1rem;

    strong {
      font-size: 1rem;
      margin-bottom: 0.45rem;
    }

    span {
      font-size: 0.9rem;
    }
  }

  .drop-zone {
    height: 13rem;
    padding: 1rem;
    text-align: center;
  }

  .psd-icon {
    width: 4.5rem;
    height: 5.4rem;
    font-size: 1.2rem;
  }

  .upload-rules li {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .dress-row,
  .confirm-row {
    gap: 0.55rem;
    align-items: center;
    padding: 0.65rem;
    min-height: auto;
  }

  .dress-row {
    grid-template-columns: 3.2rem minmax(0, 1fr) 2.8rem;
  }

  .confirm-row {
    grid-template-columns: 3rem 3rem minmax(0, 1fr);
    grid-template-rows: repeat(2, auto);
    column-gap: 0.5rem;
    row-gap: 0.45rem;
  }

  .confirm-header {
    display: none;
  }

  .dress-row img,
  .confirm-row > img,
  .thumb-box {
    width: 3rem;
    height: 3rem;
  }

  .confirm-row > img {
    grid-column: 1;
    grid-row: 1 / 3;
  }

  .thumb-box {
    grid-column: 2;
    grid-row: 1 / 3;
  }

  .dress-row__info {
    display: none;
  }

  .dress-row .pill {
    grid-column: auto;
    width: 100%;
    min-height: 2.35rem;
    padding: 0.35rem 0.75rem;
    font-size: 0.9rem;
  }

  .dress-row .text-btn {
    grid-column: auto;
    justify-self: end;
    min-width: 2.6rem;
    height: 2.35rem;
    font-size: 0.9rem;
  }

  .confirm-row .segmented {
    grid-column: 1 / -1;
  }

  .confirm-row > div:nth-of-type(2) {
    display: none;
    min-width: 0;

    strong,
    span,
    p,
    small {
      overflow-wrap: anywhere;
    }
  }

  .confirm-row > div:nth-of-type(2) {
    strong {
      font-size: 0.95rem;
      margin-bottom: 0.15rem;
    }

    p {
      display: none;
    }
  }

  .thumb-box span {
    bottom: -0.95rem;
    font-size: 0.78rem;
  }

  .segmented {
    width: 100%;
    overflow: visible;
    justify-content: flex-start;
    gap: 0.45rem;

    button {
      flex: 1 1 0;
      min-width: 0;
      min-height: 2.15rem;
      padding: 0.35rem 0.45rem;
      font-size: 0.85rem;
    }
  }

  .segmented--type {
    padding-right: 0.75rem;

    &::after {
      height: 2.15rem;
    }
  }

  .confirm-row .segmented--type {
    grid-column: 3;
    grid-row: 1;
    padding-right: 0;

    &::after {
      display: none;
    }
  }

  .confirm-row .segmented:not(.segmented--type) {
    grid-column: 3;
    grid-row: 2;
  }

  .confirm-row .segmented {
    align-self: start;
    width: 100%;
    gap: 0.3rem;

    button {
      min-height: 1.8rem;
      font-size: 0.72rem;
      padding: 0.25rem 0.2rem;
    }
  }

  .summary-card {
    padding: 0.85rem;

    h2 {
      display: inline-block;
      margin: 0 0.8rem 0.45rem 0;
    }

    strong {
      font-size: 1.7rem;
      margin: 0 0.35rem 0.35rem 0;
    }

    span,
    p {
      font-size: 0.84rem;
      line-height: 1.35;
    }

    .primary-btn {
      height: 2.45rem;
      margin: 0.6rem 0 0.35rem;
    }

    > p:last-child {
      display: none;
    }
  }

  .confirm-modal,
  .submit-lock-modal {
    width: calc(100vw - 2rem);
    max-width: none;
    box-sizing: border-box;
    padding: 1.4rem 1rem;
    border-radius: 0.875rem;

    h2 {
      margin-bottom: 0.75rem;
      font-size: 1.25rem;
    }

    p {
      font-size: 0.95rem;
      line-height: 1.7;
    }
  }

  .confirm-modal__actions {
    gap: 0.75rem;
    margin-top: 1.25rem;

    button {
      flex: 1 1 0;
      min-width: 0;
      height: 2.55rem;
      padding: 0 0.75rem;
      font-size: 0.92rem;
    }
  }

  .type-modal,
  .thumb-modal {
    width: calc(100vw - 1.25rem);
    max-height: calc(100dvh - 1.25rem);
    box-sizing: border-box;
    overflow: auto;
    padding: 1rem;
  }

  .type-modal__header {
    gap: 0.75rem;

    h3 {
      font-size: 1.35rem;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      height: 2rem;
      padding: 0 0.85rem;
      font-size: 0.86rem;
    }
  }

  .type-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    margin-top: 0.85rem;

    button {
      min-height: 2.75rem;
      padding: 0.3rem 0.25rem;
      font-size: 0.95rem;
      line-height: 1.2;
    }

    img {
      display: none;
    }
  }

  .thumb-modal__body {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .thumb-modal h2 {
    font-size: 1.35rem;
  }

  .crop-stage {
    width: 100%;
    height: 15.5rem;
    aspect-ratio: auto;
  }

  .crop-frame {
    width: min(14rem, 100%);
    height: min(14rem, calc(100vw - 3.5rem));
    aspect-ratio: auto;
  }

  .crop-frame__hint {
    bottom: 0.5rem;
    padding: 0.35rem 0.55rem;
    font-size: 0.68rem;
  }

  .preview-square {
    width: 8rem;
    height: 8rem;
    aspect-ratio: auto;
    margin: 0.65rem 0 0.75rem;
  }

  .scale-header,
  .scale-control {
    width: 100%;
  }

  .color-row {
    gap: 0.85rem;
    margin: 0.6rem 0 0.9rem;

    button {
      width: 2.5rem;
      height: 2.5rem;
    }
  }

  .thumb-modal__actions {
    gap: 0.75rem;
    position: sticky;
    bottom: 0;
    margin-top: 0.9rem;
    padding-top: 0.75rem;
    background: #fff;

    button {
      flex: 1;
    }
  }
}
</style>
